// Per-waiter × shift reconciliation for the bar day: tickets, cash / card / room / QR totals — so cash can be counted per person.
import React from 'react';
import { Users } from 'lucide-react';
import { money } from './BarTab';

const SHIFTS = [
  { id: 'morning', label: 'Morning 07–15', from: 7, to: 15 },
  { id: 'afternoon', label: 'Afternoon 15–23', from: 15, to: 23 },
  { id: 'night', label: 'Night 23–07', from: 23, to: 7 },
];
const localHour = (iso) => Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Europe/Podgorica' }).format(new Date(iso)));
export const shiftOf = (iso) => { const h = localHour(iso); return h >= 7 && h < 15 ? 'morning' : h >= 15 && h < 23 ? 'afternoon' : 'night'; };

// Pure: tickets → [{shift, staff, count, total, cash, card, room, inroom}] sorted by shift then total; void tickets excluded.
export const staffShiftRows = (tickets) => {
  const acc = {};
  (tickets || []).forEach((t) => {
    if (t.status === 'void') return;
    const who = t.served_by || t.by || '—';
    const key = `${shiftOf(t.at)}|${who}`;
    const r = acc[key] || (acc[key] = { shift: shiftOf(t.at), staff: who, count: 0, total: 0, cash: 0, card: 0, room: 0, inroom: 0 });
    r.count += 1; r.total += Number(t.total || 0);
    if (r[t.payment] !== undefined) r[t.payment] += Number(t.total || 0);
  });
  const order = { morning: 0, afternoon: 1, night: 2 };
  return Object.values(acc).sort((a, b) => order[a.shift] - order[b.shift] || b.total - a.total);
};

export const StaffShifts = ({ tickets }) => {
  const rows = staffShiftRows(tickets);
  if (!rows.length) return null;
  const sum = (k) => rows.reduce((s, r) => s + r[k], 0);
  const body = rows.map((r) => (
    <tr key={`${r.shift}-${r.staff}`} className="border-t" data-testid={`staff-shift-${r.shift}-${r.staff}`}>
      <td className="py-1.5 pl-2 pr-3 text-slate-500 whitespace-nowrap">{SHIFTS.find((s) => s.id === r.shift)?.label}</td>
      <td className="py-1.5 pr-3 font-semibold text-slate-900">{r.staff === 'guest' ? 'QR orders (guest)' : r.staff}</td>
      <td className="py-1.5 pr-3 text-right">{r.count}</td>
      <td className="py-1.5 pr-3 text-right font-semibold text-emerald-700" data-testid={`staff-cash-${r.shift}-${r.staff}`}>{money(r.cash)}</td>
      <td className="py-1.5 pr-3 text-right">{money(r.card)}</td>
      <td className="py-1.5 pr-3 text-right">{money(r.room)}</td>
      <td className="py-1.5 pr-3 text-right">{money(r.inroom)}</td>
      <td className="py-1.5 pr-2 text-right font-bold">{money(r.total)}</td>
    </tr>
  ));
  return (
    <div className="bg-white border rounded-xl p-4" data-testid="bar-staff-shifts">
      <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-sm"><Users className="w-4 h-4" /> Sales per waiter & shift <span className="text-[11px] font-normal text-slate-500">cash to hand in per person · void tickets excluded · Podgorica time</span></h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-500"><th className="py-1 pl-2 pr-3">Shift</th><th className="pr-3">Staff</th><th className="pr-3 text-right">Tickets</th><th className="pr-3 text-right">Cash</th><th className="pr-3 text-right">Card</th><th className="pr-3 text-right">Room</th><th className="pr-3 text-right">QR / pay-at-bar</th><th className="pr-2 text-right">Total</th></tr></thead>
          <tbody>{body}</tbody>
          <tfoot><tr className="border-t-2 font-semibold"><td className="py-1.5 pl-2" colSpan={2}>Day total</td><td className="text-right pr-3">{sum('count')}</td><td className="text-right pr-3 text-emerald-700" data-testid="staff-cash-total">{money(sum('cash'))}</td><td className="text-right pr-3">{money(sum('card'))}</td><td className="text-right pr-3">{money(sum('room'))}</td><td className="text-right pr-3">{money(sum('inroom'))}</td><td className="text-right pr-2">{money(sum('total'))}</td></tr></tfoot>
        </table>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">"QR / pay-at-bar" = guest orders from the terrace QR not charged to a room — the waiter who serves collects; they are attributed to the waiter who marked the order delivered; undelivered ones show under "QR orders (guest)".</p>
    </div>
  );
};

export default StaffShifts;
